import { memo, useMemo } from "react";
import { useRouteLoaderData } from "@remix-run/react";

import { track } from "~/utils/track";
import { Box } from "~/components/box/Box";
import { ReflectionCard } from "~/components/reflectionCard/ReflectionCard";
import { SidePanelLayout } from "~/components/sidePanelLayout/SidePanelLayout";
import { useLabels } from "~/hooks/useLabels";

import type { ReflectionCardSaveEvent } from "~/components/reflectionCard/ReflectionCard";
import type { TrackEventData } from "~/utils/track";
import type { SessionRouteLoaderData } from "./route";

export interface SessionReflectionProps {
  onReflectionSave: (data: { id: string; value: string }) => Promise<void>;
  onRequestClose?: () => void;
  isDesktopLayout?: boolean;
}

export const SessionReflection = memo(function SessionReflection({
  onReflectionSave,
  onRequestClose,
  isDesktopLayout,
}: SessionReflectionProps) {
  const { t } = useLabels();

  const data = useRouteLoaderData(
    "routes/$class_.sessions.$session",
  ) as SessionRouteLoaderData;

  const trackingData = useMemo<TrackEventData>(() => {
    return {
      className: data.session.class?.slug,
      sessionNumber: data.session.position,
      trigger: "sessionNotebook",
    };
  }, [data.session.class?.slug, data.session.position]);

  const content = useMemo(() => {
    return (
      <div className="SessionReflection">
        <Box direction="column" gap="var(--size-2)">
          {data.session.reflections?.map((reflection) => {
            const handleRequestEdit = () => {
              // Only track new notes, not edits to existing notes
              if (!reflection.user?.content) {
                track("crReflectionStart", trackingData);
              }
            };

            const handleSave = async (event: ReflectionCardSaveEvent) => {
              await onReflectionSave({
                id: reflection.id,
                value: event.value,
              });

              track("crReflectionSave", trackingData);
            };

            return (
              <ReflectionCard
                key={reflection.id}
                question={reflection.questionHtml}
                response={reflection.responseHtml}
                userResponse={reflection.user?.content ?? undefined}
                userResponseHtml={reflection.user?.contentHtml ?? undefined}
                onRequestEdit={handleRequestEdit}
                onSave={handleSave}
              />
            );
          })}
        </Box>
      </div>
    );
  }, [data.session.reflections, onReflectionSave, trackingData]);

  if (isDesktopLayout) {
    return (
      <SidePanelLayout
        title={t("session.tab.reflection")}
        onRequestClose={onRequestClose}
      >
        {content}
      </SidePanelLayout>
    );
  }

  return content;
});
